import { BUDGET_CAPTURE_CROP_PADDING, BUDGET_CAPTURE_MAX_OUTPUT_WIDTH } from '@constants'
import { PerspectiveCamera, Vector2 } from 'three'
import type { Item, LinkedItem, SceneObjects } from '@types'

import { getCameraCaptureContext } from './cameraControlsRegistry'
import {
  computeBudgetCaptureCropRect,
  computeBudgetCapturePose,
  computeContentAwareCropRect,
  computeItemsWorldBoundingBox,
} from './computeBudgetCapturePose'
import { prepareBudgetCaptureSubtleShadows } from './prepareBudgetCaptureSubtleShadows'

const _size = new Vector2()

function canvasToDataUrl(
  source: HTMLCanvasElement,
  crop: { x: number; y: number; width: number; height: number } | null,
  maxWidth: number,
): string {
  const srcX = crop?.x ?? 0
  const srcY = crop?.y ?? 0
  const srcW = crop?.width ?? source.width
  const srcH = crop?.height ?? source.height
  if (srcW < 8 || srcH < 8) return ''

  const scale = Math.min(1, maxWidth / srcW)
  const outW = Math.max(1, Math.round(srcW * scale))
  const outH = Math.max(1, Math.round(srcH * scale))

  const out = document.createElement('canvas')
  out.width = outW
  out.height = outH
  const ctx = out.getContext('2d')
  if (!ctx) return ''
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(source, srcX, srcY, srcW, srcH, 0, 0, outW, outH)

  const dataUrl = out.toDataURL('image/jpeg', 0.92)
  return dataUrl.length > 100 ? dataUrl : ''
}

/**
 * Captura la escena para presupuesto con una cámara dedicada (vista cenital).
 * No muta la PerspectiveCamera ni los CameraControls del usuario.
 * Tras el render (o ante error) re-renderiza con la cámara de trabajo y restaura sombras.
 */
export function captureSceneForBudget(
  items: Map<string, SceneObjects | Item | LinkedItem>,
): string {
  const ctx = getCameraCaptureContext()
  if (!ctx) return ''

  const { gl, scene, camera: userCamera } = ctx
  if (!gl?.domElement || !scene || !userCamera) return ''

  const prevRenderTarget = gl.getRenderTarget()
  const captureCamera = new PerspectiveCamera()
  let restoreShadows: (() => void) | null = null

  try {
    gl.getSize(_size)
    const cssWidth = Math.max(_size.x, 1)
    const cssHeight = Math.max(_size.y, 1)
    const aspect = cssWidth / cssHeight

    const box = computeItemsWorldBoundingBox(items)
    if (!box) {
      gl.setRenderTarget(null)
      gl.render(scene, userCamera)
      return canvasToDataUrl(gl.domElement, null, BUDGET_CAPTURE_MAX_OUTPUT_WIDTH)
    }

    const pose = computeBudgetCapturePose(box, items, { aspect })

    captureCamera.fov = pose.fov
    captureCamera.aspect = aspect
    captureCamera.near = pose.near
    captureCamera.far = pose.far
    captureCamera.position.copy(pose.position)
    captureCamera.up.copy(pose.up)
    captureCamera.lookAt(pose.target)
    captureCamera.updateProjectionMatrix()
    captureCamera.updateMatrixWorld(true)

    restoreShadows = prepareBudgetCaptureSubtleShadows(scene, gl, pose.target, box)

    gl.setRenderTarget(null)
    gl.render(scene, captureCamera)

    const canvas = gl.domElement
    const crop =
      computeContentAwareCropRect(canvas, BUDGET_CAPTURE_CROP_PADDING) ??
      computeBudgetCaptureCropRect(
        box,
        captureCamera,
        canvas.width,
        canvas.height,
        BUDGET_CAPTURE_CROP_PADDING,
      )

    const dataUrl = canvasToDataUrl(canvas, crop, BUDGET_CAPTURE_MAX_OUTPUT_WIDTH)

    restoreShadows()
    restoreShadows = null
    gl.render(scene, userCamera)

    return dataUrl
  } catch (error) {
    console.error('captureSceneForBudget failed:', error)
    try {
      restoreShadows?.()
      restoreShadows = null
      gl.setRenderTarget(null)
      gl.render(scene, userCamera)
    } catch {
      // ignore restore render errors
    }
    return ''
  } finally {
    restoreShadows?.()
    gl.setRenderTarget(prevRenderTarget)
  }
}
